// Admin CertificatesPage — "Ready to issue" (completed enrollments with no
// certificate row yet) and "Issued certificates" (paginated ledger).
import { useEffect, useState } from "react";
import { useFetch } from "../../hooks/useApi.js";
import CertificateIssueRow from "../../components/admin/CertificateIssueRow.js";
import { formatDate } from "../../lib/formatDateTime.js";
import type { CertificateReady, IssuedCertificate, IssueCertificateResult } from "../../lib/adminCertificateTypes.js";
import type { PaginatedResult } from "@dm-lms/shared/types";

export default function CertificatesPage() {
  const {
    data: readyData,
    isLoading: readyLoading,
    error: readyError,
  } = useFetch<PaginatedResult<CertificateReady>>("/api/admin/certificates/pending?page=1&pageSize=50");

  const [readyList, setReadyList] = useState<CertificateReady[]>([]);
  useEffect(() => {
    if (readyData) setReadyList(readyData.items);
  }, [readyData]);

  const [issuedPage, setIssuedPage] = useState(1);
  const {
    data: issuedData,
    isLoading: issuedLoading,
    error: issuedError,
  } = useFetch<PaginatedResult<IssuedCertificate>>(`/api/certificates?page=${issuedPage}&pageSize=20`);

  const [issuedList, setIssuedList] = useState<IssuedCertificate[]>([]);
  useEffect(() => {
    if (issuedData) setIssuedList(issuedData.items);
  }, [issuedData]);

  function handleIssued(item: CertificateReady, certificate: IssueCertificateResult) {
    setReadyList((prev) => prev.filter((r) => r.enrollmentId !== item.enrollmentId));
    setIssuedList((prev) => [
      { ...certificate, studentName: item.studentName, courseTitle: item.courseTitle },
      ...prev,
    ]);
  }

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Certificates</h1>

      <section className="mb-8">
        <h2 className="mb-3 text-lg font-medium text-dm-navy">Ready to issue</h2>

        {readyLoading && <p className="text-sm text-dm-slate">Loading…</p>}
        {readyError && <p className="text-sm text-red-600">{readyError}</p>}

        {!readyLoading &&
          !readyError &&
          (readyList.length === 0 ? (
            <p className="text-sm text-dm-slate">No certificates ready to issue.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-dm-slate">
                  <tr>
                    <th className="px-4 py-3 font-medium">Student</th>
                    <th className="px-4 py-3 font-medium">Course</th>
                    <th className="px-4 py-3 font-medium">Completed</th>
                    <th className="px-4 py-3 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {readyList.map((item) => (
                    <CertificateIssueRow
                      key={item.enrollmentId}
                      item={item}
                      onIssued={(certificate) => handleIssued(item, certificate)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </section>

      <section>
        <h2 className="mb-3 text-lg font-medium text-dm-navy">Issued certificates</h2>

        {issuedLoading && <p className="text-sm text-dm-slate">Loading…</p>}
        {issuedError && <p className="text-sm text-red-600">{issuedError}</p>}

        {!issuedLoading &&
          !issuedError &&
          (issuedList.length === 0 ? (
            <p className="text-sm text-dm-slate">No certificates issued yet.</p>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-dm-slate">
                    <tr>
                      <th className="px-4 py-3 font-medium">Code</th>
                      <th className="px-4 py-3 font-medium">Student</th>
                      <th className="px-4 py-3 font-medium">Course</th>
                      <th className="px-4 py-3 font-medium">Issued</th>
                      <th className="px-4 py-3 font-medium">Download</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {issuedList.map((cert) => (
                      <tr key={cert.id}>
                        <td className="px-4 py-3 font-medium text-dm-navy">{cert.code}</td>
                        <td className="px-4 py-3">{cert.studentName}</td>
                        <td className="px-4 py-3">{cert.courseTitle}</td>
                        <td className="px-4 py-3">{formatDate(cert.issuedAt)}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            disabled
                            title="R2 not wired yet"
                            className="cursor-not-allowed rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-400"
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
                  disabled={issuedPage <= 1}
                  onClick={() => setIssuedPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="text-xs text-dm-slate">
                  Page {issuedData?.page ?? 1} · {issuedData?.total ?? 0} total
                </span>
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
                  disabled={!issuedData?.hasMore}
                  onClick={() => setIssuedPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </>
          ))}
      </section>
    </div>
  );
}
